<template>
  <v-card
    flat
    height="100%"
    :class="{ expanded: expanded, 'show-more': showMoreLink }"
  >
    <div class="hyp-risk-content-container">
      <v-select
        v-if="subRiskTypes && subRiskTypes.length > 0"
        v-model="selectedSubRiskType"
        :items="subRiskTypes"
        item-text="name"
        item-value="name"
        :label="title"
        class="pt-1 my-0 hyp-risk-content-select"
        clearable
        :menu-props="{ contentClass: 'hyp-risk-content-select-item' }"
      >
      </v-select>
      <div
        v-if="!subRiskTypes || subRiskTypes.length < 1"
        class="details-heading application-heading mb-4"
      >
        {{ title }}
      </div>
      <v-row v-if="riskBody" class="risk-body-container">
        <v-col cols="12" class="pt-0 content-container">
          <v-card
            flat
            outlined
            color="transparent"
            ref="risksContentBody"
            v-html="riskBody"
            class="mt-0 pt-0"
          >
          </v-card>
          <v-card-actions v-if="showMoreLink && useShowMoreLink"
            ><a v-if="!expanded" @click="expand">Show more</a
            ><a v-if="expanded" @click="condense">Show less</a></v-card-actions
          >
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import lodashRemove from "lodash/remove";
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";

import { hasGrdAccess } from "@/services/user/user.service";

import { getRiskRatingCategories } from "@/services/controlRisks/risks.service";

import { RISK_RATING_PRIMARIES } from "@/common/constants/controlRisks/risks";

const EMITTED_ACTIONS = {
  EXPANDED: "expanded",
};

export default {
  name: "RisksContentBody",
  components: {},
  props: {
    title: {
      type: String,
    },
    body: {
      type: String,
    },
    publishDate: {
      type: String,
    },
    riskContent: {
      type: Array,
      required: true,
    },
    selectedPrimaryRiskCategory: {
      type: Object,
      required: false,
    },
    mainOverview: {
      type: Object,
    },
    showMoreLink: {
      type: Boolean,
      default: true,
    },
    expandedInParent: {
      type: Boolean,
      default: false,
    },
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      subRiskTypes: null,
      selectedSubRiskType: null,
      riskBody: null,
      expanded: false,
      useShowMoreLink: true,
    };
  },
  updated() {
    this.resetShowMoreLink();
  },
  methods: {
    async getFilteredSubRiskTypes() {
      let riskRatingCategories = await getRiskRatingCategories();
      lodashRemove(riskRatingCategories, (r) => {
        let hasRating = false;
        for (let i = 0; i < this.riskContent.length; i++) {
          if (
            lodashFind(this.riskContent[i].risks, ["id", r.id]) &&
            this.riskContent[i].bodyOriginal?.en
          ) {
            hasRating = true;
            break;
          }
        }
        return (
          r.parentId !== this.selectedPrimaryRiskCategory.riskCategoryId ||
          !hasRating
        );
      });
      return riskRatingCategories;
    },
    expand() {
      this.expanded = true;
    },
    condense() {
      this.expanded = false;
    },
    resetShowMoreLink() {
      if (
        this.$refs.risksContentBody?.$el?.children?.length &&
        (this.$refs.risksContentBody?.$el?.children?.length > 1 ||
          this.$refs.risksContentBody?.$el?.children[0].innerHTML.length > 110)
      ) {
        this.useShowMoreLink = true;
      } else {
        this.useShowMoreLink = false;
      }
    },
  },
  watch: {
    expanded: {
      handler(newVal) {
        this.$emit(EMITTED_ACTIONS.EXPANDED, newVal);
      },
    },
    expandedInParent: {
      handler(newVal) {
        if (newVal !== this.expanded) {
          this.expanded = newVal;
        }
      },
    },
    body: {
      handler() {
        this.riskBody = lodashCloneDeep(this.body);
      },
      immediate: true,
    },
    selectedPrimaryRiskCategory: {
      async handler() {
        this.expanded = false;

        if (this.selectedPrimaryRiskCategory) {
          this.subRiskTypes = await this.getFilteredSubRiskTypes();
          if (
            this.selectedPrimaryRiskCategory.riskCategoryId !==
              RISK_RATING_PRIMARIES.cyber &&
            !hasGrdAccess()
          ) {
            this.subRiskTypes.unshift({
              name: "Overview",
            });
          }
        } else {
          this.subRiskTypes = null;
        }
      },
      immediate: true,
    },
    selectedSubRiskType: lodashDebounce(function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.expanded = false;

        if (
          !this.selectedPrimaryRiskCategory &&
          this.selectedSubRiskType === "Overview"
        ) {
          this.riskBody = this.mainOverview?.body || "";
        } else if (
          this.selectedPrimaryRiskCategory &&
          this.selectedSubRiskType
        ) {
          let i = 0,
            found;
          while (!found && i < this.riskContent.length) {
            const thisRiskContent = this.riskContent[i];

            //Use the "overview" body for a top-level risk category
            if (
              this.selectedSubRiskType.toLowerCase() === "overview" &&
              thisRiskContent.risks?.length === 1 &&
              thisRiskContent.risks[0].name?.toLowerCase() ===
                this.selectedPrimaryRiskCategory.riskCategoryName
            ) {
              this.riskBody = thisRiskContent.body;
              found = true;
            } else {
              //Find the body for the sub risk category that was selected
              if (
                thisRiskContent.riskTypes.find(
                  (r) =>
                    r.toLowerCase() === this.selectedSubRiskType.toLowerCase()
                )
              ) {
                let body = thisRiskContent.body;
                if (body === `<p style="background: white;">&nbsp;</p>`) {
                  body = this.body;
                }
                this.riskBody = body;
                found = true;
              }
            }
            i++;
          }
        } else {
          this.riskBody = lodashCloneDeep(this.body);
        }

        this.resetShowMoreLink();
      }
    }, 500),
  },
};
</script>

<style scoped>
.details-heading {
  font-size: 20px;
}
.v-input + .risk-body-container {
  height: calc(100% - 60px);
}
.risk-body-container {
  height: 100%;
}
.show-more .risk-body-container {
  height: calc(100% - 30px);
}
.hyp-risks-content-expanded .risk-body-container {
  height: 100%;
}
.content-container {
  font-size: 14px;
  height: 100%;
}
.show-more .content-container .v-card {
  margin-bottom: 10px;
  overflow: hidden;
  height: 3em;
}
.expanded-content .show-more .content-container .v-card {
  height: auto;
}
.content-container > .v-card {
  overflow-y: auto;
  padding-right: 10px;
  padding-bottom: 20px;
  height: 100%;
}
.hyp-risk-grid .content-container {
  max-width: unset !important;
}
.hyp-risk-content-container {
  height: 100%;
}
.hyp-risks-content-expanded .hyp-risk-content-container {
  max-width: 50vw;
  margin: 12px auto;
}
.hyp-risks-content-expanded .v-card__actions {
  display: none;
}
.v-card__actions {
  justify-content: flex-end;
}
.v-card__actions > a {
  color: unset;
  font-size: 14px;
}
</style>

<style>
.hyp-risk-content-select .v-select__selection,
.hyp-risk-content-select-item .v-list-item .v-list-item__title {
  text-transform: capitalize !important;
}
</style>
